import { useEffect, useRef, useState } from 'react';

/*
 * Custom hook for detecting if an element is in view
 * Returns a ref to attach to the element and a boolean indicating if it's in view
 * or close to being in view (based on rootMargin and threshold).
 * rootMargin: CSS margin-like string to expand or contract the bounding box of the root
 * threshold: A number (between 0 and 1) that defines what fraction of the target’s bounding box must be visible before the callback fires.
 * enabled: Boolean for whether or not the observer is not created
 * once: Boolean for whether or not the observer disconnects after the first time the element comes into view
 * Cleans up observer on unmount
 */
const useElementInView = ({ root = null, rootMargin = '0px', threshold = 0, enabled = true, once = false } = {}) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  const observer = new IntersectionObserver(
    ([entry]) => {
      setInView(entry.isIntersecting);
      if (once && entry.isIntersecting) {
        observer.disconnect();
      }
    },
    { root, rootMargin, threshold }
  );

  useEffect(() => {
    if (!ref.current || !enabled) return;
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [root, rootMargin, threshold, enabled]);

  return [ref, inView];
};

export default useElementInView;
